import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { contactsApi } from '../../api/contacts-api';
import { useAuth } from '../../auth/AuthContext';
import { ErrorState } from '../../components/ErrorState';
import { PageLoadingState } from '../../components/PageLoadingState';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
  contactLevelLabels,
  contactName,
  contactPurposeLabels,
  contactPurposes,
  isStaffContact,
  scopeLabel,
  type Contact,
  type ContactLevel,
  type ContactPurpose,
} from '../../domain/contact';
import type { Site } from '../../domain/inventory';
import { toMap } from '../../lib/collections';
import { ContactDrawer } from './ContactDrawer';

interface CustomerContactsTabProps {
  customerId: string;
  customerName: string;
  sites: readonly Site[];
}

type LevelFilter = '' | ContactLevel | 'NONE';

function matchesSearch(
  contact: Contact,
  needle: string,
  sitesById: ReadonlyMap<string, Site>,
): boolean {
  if (!needle) return true;
  const text = [
    contactName(contact),
    contact.email,
    contact.phone,
    contact.title,
    contact.role,
    contact.userId,
    ...contact.scopes.map((scope) => scopeLabel(scope, sitesById)),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return text.includes(needle);
}

/**
 * A customer's contacts (tidaltech/koi#8359): who they are and where they
 * apply, with filters by level and purpose.
 */
export function CustomerContactsTab({ customerId, customerName, sites }: CustomerContactsTabProps) {
  const { canMutate } = useAuth();
  const [search, setSearch] = useState('');
  const [level, setLevel] = useState<LevelFilter>('');
  const [purpose, setPurpose] = useState<'' | ContactPurpose>('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<Contact | 'new' | null>(null);
  const sitesById = useMemo(() => toMap([...sites]), [sites]);
  const contacts = useQuery({
    queryKey: ['contacts', 'customer', customerId, includeInactive],
    queryFn: () => contactsApi.listCustomerContacts(customerId, includeInactive),
  });

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (contacts.data ?? [])
      .filter((contact) => matchesSearch(contact, needle, sitesById))
      .filter((contact) => {
        if (level === 'NONE') return contact.scopes.length === 0;
        return (
          contact.scopes.some(
            (scope) =>
              (!level || scope.level === level) && (!purpose || scope.purposes.includes(purpose)),
          ) ||
          (!level && !purpose)
        );
      })
      .sort((a, b) => contactName(a).localeCompare(contactName(b)));
  }, [contacts.data, level, purpose, search, sitesById]);

  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <h2>Contacts</h2>
          <p className="contact-subtitle">
            People to talk to at {customerName}’s sites, regions or the whole customer, and Tidal’s
            own owners.
          </p>
        </div>
        <div className="panel-heading-actions">
          {contacts.isSuccess ? (
            <span className="panel-context">
              {rows.length.toLocaleString()} {rows.length === 1 ? 'contact' : 'contacts'}
            </span>
          ) : null}
          <button
            type="button"
            className="primary"
            onClick={() => setEditing('new')}
            disabled={!canMutate}
            title={canMutate ? undefined : 'Sign in to add contacts'}
          >
            Add contact
          </button>
        </div>
      </div>

      <form
        className="customer-site-controls contact-filters"
        role="search"
        aria-label="Filter contacts"
        onSubmit={(event) => event.preventDefault()}
      >
        <label>
          <span>Search</span>
          <input
            type="search"
            aria-label="Search contacts"
            placeholder="Name, email, phone or site"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label>
          <span>Level</span>
          <SearchableSelect
            ariaLabel="Level"
            value={level}
            onChange={(value) => setLevel(value as LevelFilter)}
            options={[
              { value: '', label: 'Any level' },
              ...(['SITE', 'REGION', 'CUSTOMER'] as const).map((value) => ({
                value,
                label: value === 'CUSTOMER' ? 'Whole customer' : contactLevelLabels[value],
              })),
              { value: 'NONE', label: 'Not attached' },
            ]}
            placeholder="Any level"
          />
        </label>
        <label>
          <span>Purpose</span>
          <SearchableSelect
            ariaLabel="Purpose"
            value={purpose}
            onChange={(value) => setPurpose(value as '' | ContactPurpose)}
            options={[
              { value: '', label: 'Any purpose' },
              ...contactPurposes.map((value) => ({ value, label: contactPurposeLabels[value] })),
            ]}
            placeholder="Any purpose"
          />
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
          Show former contacts
        </label>
      </form>

      {contacts.isLoading ? (
        <PageLoadingState label="Loading contacts" className="section-loading-state" />
      ) : null}
      {contacts.isError ? (
        <ErrorState
          title="Contacts could not be loaded"
          rpcName="ListContacts"
          onRetry={() => void contacts.refetch()}
        />
      ) : null}
      {contacts.isSuccess && rows.length === 0 ? (
        <p className="empty-note">
          {contacts.data.length === 0 ? 'No contacts yet.' : 'No contacts match these filters.'}
        </p>
      ) : null}
      {rows.length > 0 ? (
        <div className="summary-table contact-table">
          <table>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email and phone</th>
                <th scope="col">Where and for what</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((contact) => (
                <tr
                  key={contact.id}
                  className={contact.inactive ? 'contact-row-former' : undefined}
                >
                  <th scope="row">
                    <strong>{contactName(contact)}</strong>
                    {isStaffContact(contact) ? (
                      <span className="state-badge contact-badge">Tidal</span>
                    ) : null}
                    {contact.inactive ? (
                      <span className="state-badge contact-badge">Former</span>
                    ) : null}
                    {contact.title || contact.role ? (
                      <small className="table-subtext">
                        {[contact.title, contact.role].filter(Boolean).join(' · ')}
                      </small>
                    ) : null}
                  </th>
                  <td>
                    {contact.email ? <a href={`mailto:${contact.email}`}>{contact.email}</a> : null}
                    {contact.phone ? (
                      <small className="table-subtext">{contact.phone}</small>
                    ) : null}
                    {!contact.email && !contact.phone ? (
                      <span className="contact-muted">—</span>
                    ) : null}
                  </td>
                  <td>
                    {contact.scopes.length === 0 ? (
                      <span className="contact-muted">Not attached</span>
                    ) : null}
                    <ul className="contact-scope-list">
                      {contact.scopes.map((scope, index) => (
                        <li key={index}>
                          <strong>{scopeLabel(scope, sitesById)}</strong>
                          {scope.primary ? (
                            <span className="state-badge contact-badge-primary">Primary</span>
                          ) : null}
                          <span className="contact-muted">
                            {' '}
                            {scope.purposes.map((p) => contactPurposeLabels[p]).join(', ')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="contact-actions">
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setEditing(contact)}
                      disabled={!canMutate}
                    >
                      Edit<span className="visually-hidden"> {contactName(contact)}</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {editing ? (
        <ContactDrawer
          customerId={customerId}
          customerName={customerName}
          sites={sites}
          contact={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </section>
  );
}
